import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

const Root = styled(Box)(() => ({ display: "block", lineHeight: 0 }));

type SparklineProps = {
  values: number[];
  /** More series drawn on the same scale as ``values`` (diastolic under systolic), dashed. */
  others?: number[][];
  /** One per series (``values`` first), written at the line's end: with two lines on one scale, each
   *  says which it is ("SYS 158"). */
  labels?: string[];
  height?: number;
  /** Semantic colour is reserved for status; a sparkline is neutral. */
  color?: string;
};

/**
 * A recorded series, drawn to its own scale.
 *
 * Deliberately unlabelled and unaxed: this shows the shape of what was
 * recorded, not a judgment about it. No thresholds, no bands, no colour that
 * would imply a reading is good or bad. Measures its container and draws at
 * that pixel width so strokes stay crisp. (Ported from the mission-agent
 * dashboard; plain SVG maths instead of d3.)
 */
export function Sparkline(props: SparklineProps) {
  const holder = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const height = props.height ?? 28;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = holder.current;
    if (!element) return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const stroke = props.color ?? theme.palette.text.secondary;
  const values = props.values;
  const all = [values, ...(props.others ?? [])]
    .map((series, i) => ({ series, label: props.labels?.[i], dashed: i > 0 }))
    .filter(({ series }) => series.length >= 2);
  const paths: { d: string; dashed: boolean }[] = [];
  const ends: [number, number][] = [];
  const tags: { text: string; x: number; y: number }[] = [];
  // Labels are never cut short or dropped: they shrink (to 8px) so the chart keeps about half the width.
  const longest = props.labels?.length ? Math.max(...props.labels.map((t) => t.length)) : 0;
  const charEm = 0.66; // a label character's width, in em, with a little spare
  const fontSize = longest ? Math.max(8, Math.min(10, (width * 0.55 - 6) / (longest * charEm))) : 10;
  // Room on the right for the longest label, plus a gap.
  const gutter = longest ? longest * fontSize * charEm + 6 : 0;
  if (all.length && width >= 8 + gutter) {
    const padding = 3;
    const pooled = all.flatMap(({ series }) => series);
    const min = Math.min(...pooled);
    const max = Math.max(...pooled);
    // A flat series would collapse to a zero-height scale and disappear.
    const span = max - min || 1;
    const lo = min - span * 0.1;
    const hi = max + span * 0.1;
    const y = (v: number) => height - padding - ((v - lo) * (height - 2 * padding)) / (hi - lo);
    for (const { series, label, dashed } of all) {
      const x = (i: number) => padding + (i * (width - gutter - 2 * padding)) / (series.length - 1);
      paths.push({
        d: series.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(""),
        dashed,
      });
      const end: [number, number] = [x(series.length - 1), y(series[series.length - 1])];
      ends.push(end);
      if (label) tags.push({ text: label, x: end[0] + 5, y: end[1] });
    }
    // Labels sit at their line's end, pushed apart top to bottom so close readings stay legible.
    tags.sort((a, b) => a.y - b.y);
    const half = fontSize / 2;
    tags.forEach((tag, i) => {
      tag.y = Math.max(tag.y, half, i ? tags[i - 1].y + fontSize + 1 : half);
    });
    for (let i = tags.length - 1; i >= 0; i -= 1) {
      tags[i].y = Math.min(tags[i].y, i < tags.length - 1 ? tags[i + 1].y - fontSize - 1 : height - half);
    }
  }

  return (
    <Root ref={holder}>
      <svg
        height={height}
        style={{ display: "block", width: "100%" }}
        role="img"
        aria-label={
          values.length ? `Trend of ${values.length} readings, latest ${values[values.length - 1]}` : "No readings"
        }
      >
        {paths.map(({ d, dashed }, i) => (
          <path
            key={i}
            d={d}
            strokeDasharray={dashed ? "3 2" : undefined}
            fill="none"
            stroke={stroke}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {/* Emphasise the latest point: the current value is what is being read. */}
        {ends.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r={2.2} fill={stroke} />
        ))}
        {tags.map((tag) => (
          <text
            key={tag.text}
            x={tag.x}
            y={tag.y}
            dominantBaseline="central"
            fontSize={fontSize}
            fontWeight={600}
            fill={stroke}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {tag.text}
          </text>
        ))}
      </svg>
    </Root>
  );
}
